//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React from 'react';
import { connect } from 'react-redux';
import Redux, { bindActionCreators } from 'redux';
import { Button, Icon, Spinner, Toggle } from 'unison';

import { ICuralateStore } from '@models/AppState';
import AnchorWidget from '@react-bridge/react-ng-widgets/anchor-widget';
import {
  closeStreamsModerationDrawer,
  resetAssetFeedsDrawer,
} from '@streams-drawer/actions/streams-drawer-actions';
import { assetFeedsModalActions } from '@streams/actions/assetFeedsModalActions';
import {
  MEDIA_SOURCE,
  MEDIA_SOURCE_MAPPINGS,
} from '@streams/constants/assetFeedsModalConstants';
import styled, { css } from 'styled-components';
import { capitalize } from '@utils/stringUtils';

function mapStateToProps(state: ICuralateStore) {
  return {
    brandInstagramUsername: state.streams.streamsDrawer.brandInstagramUsername,
    brandFacebookPageName: state.streams.streamsDrawer.brandFacebookPageName,
    sourceSettings: state.streams.assetFeedsModal.sourceSettings,
    sourceConnectionStates:
      state.streams.assetFeedsModal.sourceConnectionStates,
    isFetchingFacebook:
      state.streams.streamsDrawer.isFetchingBrandFacebookPageName,
    isFetchingInstagram:
      state.streams.streamsDrawer.isFetchingBrandInstagramUsername,
  };
}

function mapDispatchToProps(dispatch: Redux.Dispatch) {
  return {
    actions: {
      assetFeedsModal: bindActionCreators(assetFeedsModalActions, dispatch),
      closeStreamsDrawer: bindActionCreators(
        closeStreamsModerationDrawer,
        dispatch,
      ),
      resetFeedsDrawer: bindActionCreators(resetAssetFeedsDrawer, dispatch),
    },
  };
}

type Props = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;

export const StreamsDrawerAssetFeedsSourceList: React.FunctionComponent<Props> =
  (props) => {
    const {
      actions: { assetFeedsModal, closeStreamsDrawer, resetFeedsDrawer },
      brandInstagramUsername,
      isFetchingFacebook,
      isFetchingInstagram,
      sourceSettings,
      sourceConnectionStates,
    } = props;

    const isLoading = isFetchingFacebook || isFetchingInstagram;

    const handleAnchorWidgetClick = () => {
      assetFeedsModal.closeAssetFeedsModal();
      closeStreamsDrawer();
      resetFeedsDrawer();
    };

    const mediaSourceList = Object.values(MEDIA_SOURCE).map((source) => {
      const shouldUseBrandInstagramUsername =
        source === MEDIA_SOURCE.INSTAGRAM && brandInstagramUsername;

      const title = shouldUseBrandInstagramUsername
        ? `@${brandInstagramUsername}`
        : MEDIA_SOURCE_MAPPINGS[source].title;

      const subtitle = MEDIA_SOURCE_MAPPINGS[source].subtitle;
      const networkName = MEDIA_SOURCE_MAPPINGS[source].network;
      const icon = MEDIA_SOURCE_MAPPINGS[source].icon;

      const capitalizedNetwork = capitalize(networkName);

      const isSourceDisconnected =
        source !== MEDIA_SOURCE.UPLOADED &&
        !sourceConnectionStates[networkName];
      const isSourceTurnedOn = sourceSettings[source];

      return (
        <Source key={source} isDisconnected={isSourceDisconnected}>
          <div className='cu-d-flex cu-justify-content-between cu-align-items-center'>
            <div className='cu-d-flex cu-align-items-flex-start'>
              <IconContainer>
                <NetworkIcon
                  isDisconnected={isSourceDisconnected && isSourceTurnedOn}
                  name={icon}
                />
              </IconContainer>
              <SourceDescription>
                <TitleContainer>
                  <Title
                    isDisconnected={isSourceDisconnected && isSourceTurnedOn}
                    className='cu-text-subsection'
                  >
                    {title}
                  </Title>
                  {isSourceDisconnected && <AlarmIcon name='alarm' />}
                </TitleContainer>
                <Subtitle className='cu-text-body-secondary'>
                  {subtitle}
                </Subtitle>
                {isSourceDisconnected && (
                  <ErrorMessage className='cu-text-caption'>
                    {isSourceTurnedOn ? (
                      <>
                        <AnchorButton
                          className='cu-button-invisible'
                          onClick={handleAnchorWidgetClick}
                        >
                          <AnchorWidget
                            text={`Reconnect your ${capitalizedNetwork} account`}
                            state='settings'
                          />
                        </AnchorButton>
                        {` to add media from ${capitalizedNetwork} to your feed.`}
                      </>
                    ) : (
                      `Your ${capitalizedNetwork} account is not currently connected.`
                    )}
                  </ErrorMessage>
                )}
              </SourceDescription>
            </div>
            <Toggle
              isDisabled={isSourceDisconnected}
              value={isSourceTurnedOn}
              handleChange={(newValue) =>
                assetFeedsModal.changeAssetFeedsStreamSource(source, newValue)
              }
            />
          </div>
        </Source>
      );
    });

    return <>{isLoading ? <Spinner /> : mediaSourceList}</>;
  };

interface INetworkStatus {
  isDisconnected: boolean;
}

const Source = styled.div<INetworkStatus>`
  margin-bottom: ${({ theme, isDisconnected }) => css`
    ${isDisconnected ? theme.spacings[2] : theme.spacings[4]};
  `};
`;

const SourceDescription = styled.div`
  margin-left: ${({ theme }) => theme.spacings[2]};
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Title = styled.div<INetworkStatus>`
  && {
    line-height: 1;
    ${({ theme, isDisconnected }) =>
      isDisconnected &&
      css`
        color: ${theme.colors.red8};
      `}
  }
`;

const Subtitle = styled.div``;

const IconContainer = styled.div`
  height: 100%;
`;

const NetworkIcon = styled(Icon)<INetworkStatus>`
  && {
    svg {
      ${({ theme, isDisconnected }) =>
        isDisconnected &&
        `
        color: ${theme.colors.red8};
      `}
      display: inline;
      width: 13px;
      height: 13px;
    }
  }
`;

const AlarmIcon = styled(Icon)`
  svg {
    color: ${({ theme }) => theme.colors.red8};
    margin-left: ${({ theme }) => theme.spacings[1]};
  }
`;

const ErrorMessage = styled.div`
  && {
    color: ${({ theme }) => theme.colors.red8};
    text-align: left;
  }
`;

const AnchorButton = styled(Button)`
  div {
    display: inline;
  }
`;

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(StreamsDrawerAssetFeedsSourceList);
